/**
 * Export Map Merger Utilities
 *
 * Functions for merging generated export maps with original map.csv codes
 * and detecting new variables from manipulations.
 */

import {
  ExportMapVariable,
  ExportMapCategory,
  ExportMapMergeResult,
} from "src/types/exportMap.types"
import { parseCSVLine, splitCSVRecords } from "./exportMapParser"

/**
 * Entry in the original map with variable code and categories
 */
interface OriginalMapEntry {
  variableCode: string
  /** Map from label to code for quick lookup */
  categoriesMap?: Map<string, number | string>
  /** Array of categories in original order for export */
  categoriesArray?: ExportMapCategory[]
}

/**
 * Normalizes a label for comparison by collapsing multiple consecutive quotes
 * into single quotes. This handles cases where the engine returns labels with
 * different quote escaping than the original imported map.
 *
 * Example: `""Agreement""` becomes `"Agreement"`
 */
function normalizeLabel(label: string): string {
  // Collapse multiple consecutive quotes into single quotes
  return label.replace(/"{2,}/g, '"')
}

/**
 * Parses original map.csv format (may differ from export map format)
 * Typical format: LABEL, VARIABLE, ...
 *
 * @param csvContent - Raw CSV content from map.csv
 * @returns Map of fullLabel -> variableCode and categories
 */
export function parseOriginalMapCSV(
  csvContent: string
): Map<string, OriginalMapEntry> {
  const result = new Map<string, OriginalMapEntry>()

  const lines = splitCSVRecords(csvContent)
  if (lines.length === 0) return result

  // Parse header to find column indices (use proper CSV parsing for quoted values)
  const header = parseCSVLine(lines[0]).map((h) => h.trim().toUpperCase())
  const labelIdx = header.findIndex((h) => h.includes("LABEL"))
  const variableIdx = header.findIndex(
    (h) => h.includes("VARIABLE") || h.includes("CODE") || h.includes("NAME")
  )
  const valuesIdx = header.findIndex(
    (h) => h.includes("VALUES") || h.includes("CATEGORIES")
  )

  if (labelIdx === -1) return result

  for (let i = 1; i < lines.length; i++) {
    const cols = parseCSVLine(lines[i]).map((c) => c.trim())
    const label = cols[labelIdx]
    const variableCode = variableIdx >= 0 ? cols[variableIdx] : ""

    // VALUES column and everything after (may not be quoted, so spans multiple array elements)
    let valuesArr: string[] = []
    if (valuesIdx >= 0 && cols.length > valuesIdx) {
      valuesArr = cols.slice(valuesIdx).filter((v) => v !== "")
    }

    if (label) {
      const entry: OriginalMapEntry = {
        variableCode,
      }

      // Parse category codes if present
      if (valuesArr.length > 0) {
        const categoriesMap = new Map<string, number | string>()
        const categoriesArray: ExportMapCategory[] = []

        // Determine format by checking if first value is numeric (alternating code,label format)
        // or if it's label:code format (e.g., "Male:1")
        const firstValueIsNumeric = !isNaN(Number(valuesArr[0]))

        // Check for label:code format: "Label:123" where the part after last colon is numeric
        const isLabelCodeFormat =
          !firstValueIsNumeric &&
          valuesArr.some((p) => {
            if (!p.includes(":")) return false
            const parts = p.split(":")
            const lastPart = parts[parts.length - 1].trim()
            return !isNaN(Number(lastPart))
          })

        if (isLabelCodeFormat) {
          // label:code format (e.g., "Male:1,Female:2")
          for (const part of valuesArr) {
            if (part.includes(":")) {
              const colonIdx = part.lastIndexOf(":")
              const catLabel = part.substring(0, colonIdx).trim()
              const codeStr = part.substring(colonIdx + 1).trim()
              const code = isNaN(Number(codeStr)) ? codeStr : Number(codeStr)
              categoriesMap.set(catLabel, code)
              categoriesArray.push({
                label: catLabel,
                code,
                originalCode: code,
              })
            }
          }
        } else {
          // Alternating code,label format: 1,Male,2,Female,...
          for (let j = 0; j < valuesArr.length; j += 2) {
            const codeStr = valuesArr[j]
            const catLabel = valuesArr[j + 1] || codeStr // fallback to code if no label
            const code = isNaN(Number(codeStr)) ? codeStr : Number(codeStr)
            categoriesMap.set(catLabel, code)
            categoriesArray.push({ label: catLabel, code, originalCode: code })
          }
        }

        if (categoriesMap.size > 0) {
          entry.categoriesMap = categoriesMap
          entry.categoriesArray = categoriesArray
        }
      }

      // Use normalized label as key for matching (handles quote escaping differences)
      result.set(normalizeLabel(label), entry)
    }
  }

  return result
}

/**
 * Merges generated export map with original map codes.
 * - Variables in original get their codes marked as original
 * - Variables not in original are marked as new (from manipulations)
 *
 * @param generatedVariables - Variables from generated export map
 * @param originalMapCsv - Original map.csv content (optional)
 * @param useOriginalCodes - Whether to use original codes (true) or auto-generated codes (false)
 * @returns Merged result with original code markers
 */
export function mergeWithOriginalMap(
  generatedVariables: ExportMapVariable[],
  originalMapCsv?: string,
  useOriginalCodes: boolean = true
): ExportMapMergeResult {
  const hasOriginalMap = originalMapCsv && originalMapCsv.trim().length > 0
  const originalMap = hasOriginalMap
    ? parseOriginalMapCSV(originalMapCsv)
    : new Map()

  let originalCodeCount = 0
  let newVariableCount = 0

  const mergedVariables = generatedVariables.map((variable) => {
    // Use normalized label for lookup to handle quote escaping differences
    const original = originalMap.get(normalizeLabel(variable.fullLabel))

    if (original) {
      // Variable exists in original map
      originalCodeCount++

      // Use original categories array when available - it has the correct code,label structure
      // The engine's DUMP doesn't preserve original numeric codes, only labels
      let mergedCategories: ExportMapCategory[] | undefined
      if (
        useOriginalCodes &&
        original.categoriesArray &&
        original.categoriesArray.length > 0
      ) {
        // Use original categories exactly as they were in the original map
        mergedCategories = original.categoriesArray
      } else if (variable.categories) {
        // Build a normalized lookup map for original category codes
        const normalizedOriginalCodes = new Map<string, number | string>()
        if (original.categoriesMap) {
          for (const [label, code] of original.categoriesMap) {
            normalizedOriginalCodes.set(normalizeLabel(label), code)
          }
        }

        mergedCategories = variable.categories.map((cat) => {
          // Try both exact match and normalized match for category labels
          let originalCatCode = original.categoriesMap?.get(cat.label)
          if (originalCatCode === undefined) {
            originalCatCode = normalizedOriginalCodes.get(
              normalizeLabel(cat.label)
            )
          }
          return {
            ...cat,
            originalCode: originalCatCode ?? undefined,
            code:
              useOriginalCodes && originalCatCode !== undefined
                ? originalCatCode
                : cat.code,
          } as ExportMapCategory
        })
      }

      return {
        ...variable,
        // Use original code if flag is set, otherwise keep generated code
        variableCode:
          useOriginalCodes && original.variableCode
            ? original.variableCode
            : variable.variableCode,
        originalCode: original.variableCode || undefined,
        isNew: false,
        categories: mergedCategories,
      }
    } else if (hasOriginalMap) {
      // Variable not in original map but map exists - it's new (from manipulations)
      newVariableCount++
      return {
        ...variable,
        isNew: true,
      }
    } else {
      // No original map to compare against - not considered new
      return {
        ...variable,
        isNew: false,
      }
    }
  })

  return {
    variables: mergedVariables,
    originalCodeCount,
    newVariableCount,
  }
}

/**
 * Checks if a variable's current code matches the original code from map.csv
 *
 * @param variable - Variable to check
 * @returns true if current code matches original (unchanged)
 */
export function hasOriginalCode(variable: ExportMapVariable): boolean {
  if (variable.originalCode === undefined || variable.originalCode === "") {
    return false
  }
  // Check if current code matches original code
  return variable.variableCode === variable.originalCode
}

/**
 * Checks if a category's current code matches the original code from map.csv
 *
 * @param category - Category to check
 * @returns true if current code matches original (unchanged)
 */
export function hasCategoryOriginalCode(category: ExportMapCategory): boolean {
  if (category.originalCode === undefined) {
    return false
  }
  // Check if current code matches original code
  return category.code === category.originalCode
}

/**
 * Generates a variable code from a label path.
 * Used for auto-generating codes when original is not available.
 *
 * @param path - Label path segments
 * @param maxLength - Maximum code length (SPSS limits to 64 chars)
 * @returns Generated variable code
 */
export function generateVariableCode(
  path: string[],
  maxLength: number = 64
): string {
  // Use last segment, clean and truncate
  const lastSegment = path[path.length - 1] || "VAR"

  // Remove special characters, replace spaces with underscores
  let code = lastSegment
    .toUpperCase()
    .replace(/[^A-Z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")

  // SPSS variables must start with letter
  if (/^[0-9]/.test(code)) {
    code = "V_" + code
  }

  // Truncate if needed
  if (code.length > maxLength) {
    code = code.substring(0, maxLength)
  }

  return code || "VAR"
}

/**
 * Validates a variable code for SPSS compatibility.
 *
 * @param code - Variable code to validate
 * @returns Array of validation errors (empty if valid)
 */
export function validateVariableCode(code: string): string[] {
  const errors: string[] = []

  if (!code || code.trim() === "") {
    errors.push("Variable code cannot be empty")
    return errors
  }

  if (code.length > 64) {
    errors.push("Variable code must be 64 characters or less")
  }

  if (!/^[A-Za-z]/.test(code)) {
    errors.push("Variable code must start with a letter")
  }

  if (!/^[A-Za-z][A-Za-z0-9_@#$]*$/.test(code)) {
    errors.push("Variable code contains invalid characters")
  }

  // SPSS reserved words
  const reserved = [
    "ALL",
    "AND",
    "BY",
    "EQ",
    "GE",
    "GT",
    "LE",
    "LT",
    "NE",
    "NOT",
    "OR",
    "TO",
    "WITH",
  ]
  if (reserved.includes(code.toUpperCase())) {
    errors.push(`"${code}" is a reserved word in SPSS`)
  }

  return errors
}

/**
 * Finds duplicate variable codes in a list
 *
 * @param variables - Variables to check
 * @returns Map of duplicate codes to their labels
 */
export function findDuplicateCodes(
  variables: ExportMapVariable[]
): Map<string, string[]> {
  const codeToLabels = new Map<string, string[]>()

  for (const v of variables) {
    const code = v.variableCode.toUpperCase()
    const existing = codeToLabels.get(code) || []
    existing.push(v.fullLabel)
    codeToLabels.set(code, existing)
  }

  // Filter to only duplicates
  const duplicates = new Map<string, string[]>()
  for (const [code, labels] of codeToLabels) {
    if (labels.length > 1) {
      duplicates.set(code, labels)
    }
  }

  return duplicates
}

/**
 * Resolves duplicate variable codes for INT/CAT pairs.
 * When an INT and CAT variable share the same code, appends '_v' to the INT entry.
 * Only handles INT/CAT duplicates — other duplicates are left unchanged.
 *
 * @param variables - Variables to check and resolve
 * @returns New array with resolved variable codes
 */
export function resolveDuplicateCodes(
  variables: ExportMapVariable[]
): ExportMapVariable[] {
  const duplicates = findDuplicateCodes(variables)
  if (duplicates.size === 0) return variables

  // Build set of all codes for collision checking
  const allCodes = new Set(variables.map((v) => v.variableCode.toUpperCase()))

  return variables.map((v) => {
    const code = v.variableCode.toUpperCase()
    const dupLabels = duplicates.get(code)
    if (!dupLabels || dupLabels.length <= 1) return v

    // Check if this duplicate pair is specifically INT + CAT
    const dupsForCode = variables.filter(
      (dv) => dv.variableCode.toUpperCase() === code
    )
    const hasCAT = dupsForCode.some((dv) => dv.type === "CAT")
    const hasINT = dupsForCode.some((dv) => dv.type === "INT")
    if (!hasCAT || !hasINT) return v // Not an INT/CAT pair — leave alone

    // Only suffix the INT entry
    if (v.type !== "INT") return v

    let newCode = `${v.variableCode}_v`
    // Ensure no collision with existing codes
    let suffix = 2
    while (allCodes.has(newCode.toUpperCase())) {
      newCode = `${v.variableCode}_v${suffix}`
      suffix++
    }
    allCodes.add(newCode.toUpperCase())
    return { ...v, variableCode: newCode }
  })
}
